import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { QRCodeSVG } from 'qrcode.react';
import LanguageToggle from '@/components/LanguageToggle';
import { toast } from 'sonner';
import { 
  FileText, 
  Users, 
  Calendar, 
  CreditCard, 
  CheckCircle2, 
  ArrowRight,
  Smartphone,
  Globe,
  Zap,
  Shield,
  Clock,
  Menu,
  X,
  ChevronRight,
  Mail,
  ScanLine,
  Send,
  Building2,
  User,
  Loader2
} from 'lucide-react';

const LandingPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [email, setEmail] = useState('');
  
  // Contact form state
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    company: '',
    niche: '',
    phone: '',
    employees_count: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    
    if (!contactForm.name || !contactForm.email || !contactForm.company) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setIsSubmitting(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(contactForm)
      });
      
      if (response.ok) {
        setFormSubmitted(true);
        toast.success("Thanks! We'll be in touch soon.");
        setContactForm({ name: '', email: '', company: '', niche: '', phone: '', employees_count: '', message: '' });
      } else {
        throw new Error('Failed to submit');
      }
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const niches = [
    'Roofing',
    'Landscaping', 
    'Painting',
    'Concrete',
    'HVAC',
    'Plumbing',
    'Electrical',
    'General Construction',
    'Cleaning Services',
    'Fencing',
    'Flooring',
    'Other'
  ];

  const features = [
    {
      icon: FileText,
      title: 'Professional Estimates',
      description: 'Create beautiful, branded estimates in minutes. Send instantly and track when clients view them.',
      color: 'bg-blue-500'
    },
    {
      icon: CreditCard,
      title: 'Easy Invoicing',
      description: 'Generate invoices from approved estimates with one click. Accept payments online or track cash/check.',
      color: 'bg-green-500'
    },
    {
      icon: Calendar,
      title: 'Job Scheduling',
      description: 'Calendar view for all your jobs. Assign crew, track progress, and never miss an appointment.',
      color: 'bg-purple-500'
    },
    {
      icon: Users,
      title: 'Client Management',
      description: 'Keep all customer info organized. View history, notes, and communication in one place.',
      color: 'bg-amber-500'
    },
    {
      icon: Smartphone,
      title: 'Works Offline',
      description: 'Use the app even without internet. Perfect for job sites with poor connectivity.',
      color: 'bg-rose-500'
    },
    {
      icon: Globe,
      title: 'White-Label Ready',
      description: 'Your logo, your colors, your brand. Clients see your business, not ours.',
      color: 'bg-cyan-500'
    }
  ];

  const benefits = [
    { text: 'No more Word docs and spreadsheets', icon: CheckCircle2 },
    { text: 'Get paid faster with online payments', icon: CheckCircle2 },
    { text: 'Look professional to every client', icon: CheckCircle2 },
    { text: 'Track jobs from estimate to completion', icon: CheckCircle2 },
    { text: 'Access everything from your phone', icon: CheckCircle2 },
    { text: 'Bilingual support (English & Spanish)', icon: CheckCircle2 }
  ];

  // Testimonials removed - to be added when we have real reviews

  const pricingTiers = [
    {
      name: 'Portal Basic',
      price: '79',
      description: 'Perfect for solo contractors',
      features: [
        '1 user',
        'Up to 50 clients',
        'Unlimited estimates',
        'Job scheduling',
        'Invoicing & payments',
        'Calendar view',
        'Offline mode (PWA)'
      ],
      highlighted: false
    },
    {
      name: 'Portal Pro',
      price: '149',
      description: 'For growing businesses',
      features: [
        'Up to 5 users',
        'Unlimited clients',
        'Everything in Basic',
        'Crew management',
        'Automation workflows',
        'Advanced reporting',
        'Priority support',
        'Custom branding'
      ],
      highlighted: true
    }
  ];

  const handleGetStarted = () => {
    navigate('/login');
  };

  const handleDemo = () => {
    navigate('/demo');
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm border-b border-gray-100 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo - ContractorOS branding */}
            <div className="flex items-center gap-2">
              <span className="text-2xl font-black tracking-tight">
                <span className="bg-gradient-to-r from-primary to-blue-400 bg-clip-text text-transparent">Contractor</span>
                <span className="text-foreground">OS</span>
              </span>
              <span className="hidden sm:inline text-xs text-muted-foreground uppercase tracking-wider ml-2">by BirdenMedia</span>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-muted-foreground hover:text-foreground transition-colors font-medium">
                Features
              </a>
              <a href="#pricing" className="text-muted-foreground hover:text-foreground transition-colors font-medium">
                Pricing
              </a>
              <a href="#contact" className="text-muted-foreground hover:text-foreground transition-colors font-medium">
                Contact
              </a>
            </div>

            {/* Desktop CTA */}
            <div className="hidden md:flex items-center gap-3">
              <Button 
                variant="ghost" 
                onClick={handleDemo}
                className="font-semibold"
                data-testid="nav-demo-btn"
              >
                {t('lp.nav.tryDemo')}
              </Button>
              <Button 
                onClick={handleGetStarted}
                className="bg-primary hover:bg-primary/90 font-semibold"
                data-testid="nav-login-btn"
              >
                {t('lp.nav.login')}
              </Button>
              <LanguageToggle />
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 py-4 px-4 animate-fade-in">
            <div className="flex flex-col gap-4">
              <a href="#features" className="text-foreground font-medium py-2" onClick={() => setMobileMenuOpen(false)}>
                {t('lp.nav.features')}
              </a>
              <a href="#pricing" className="text-foreground font-medium py-2" onClick={() => setMobileMenuOpen(false)}>
                {t('lp.nav.pricing')}
              </a>
              <a href="#contact" className="text-foreground font-medium py-2" onClick={() => setMobileMenuOpen(false)}>
                {t('lp.nav.contact')}
              </a>
              <div className="py-3 border-t border-gray-100">
                <LanguageToggle />
              </div>
              <div className="flex flex-col gap-2 pt-2">
                <Button variant="outline" onClick={handleDemo} className="w-full">
                  {t('lp.nav.tryDemo')}
                </Button>
                <Button onClick={handleGetStarted} className="w-full bg-primary">
                  {t('lp.nav.login')}
                </Button>
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="pt-24 md:pt-32 pb-16 md:pb-24 px-4 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Hero Text */}
            <div className="text-center lg:text-left animate-fade-in">
              <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-semibold mb-6">
                <Zap className="w-4 h-4" />
                Built for contractors, by contractors
              </div>
              
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground leading-tight mb-6">
                Run Your Contracting Business{' '}
                <span className="text-primary">Like a Pro</span>
              </h1>
              
              <p className="text-lg sm:text-xl text-muted-foreground mb-8 max-w-xl mx-auto lg:mx-0">
                Stop juggling Word docs, spreadsheets, and sticky notes. 
                Get the all-in-one client portal that helps you look professional and get paid faster.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Button 
                  size="lg" 
                  onClick={handleDemo}
                  className="bg-primary hover:bg-primary/90 h-14 px-8 text-lg font-semibold shadow-lg shadow-primary/25 transition-all hover:shadow-xl hover:shadow-primary/30"
                  data-testid="hero-demo-btn"
                >
                  Try Free Demo
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button 
                  size="lg" 
                  variant="outline"
                  onClick={handleGetStarted}
                  className="h-14 px-8 text-lg font-semibold"
                  data-testid="hero-login-btn"
                >
                  Sign In
                </Button>
              </div>
              
              <p className="text-sm text-muted-foreground mt-6">
                No credit card required • Free 14-day trial
              </p>
            </div>

            {/* Hero Image / Dashboard Preview */}
            <div className="relative animate-slide-up">
              <div className="bg-gradient-to-br from-primary/5 to-secondary/5 rounded-3xl p-2 md:p-4 shadow-2xl">
                <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100">
                  {/* Mock Dashboard Header */}
                  <div className="bg-gradient-to-r from-primary to-blue-700 px-4 md:px-6 py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-white/20 rounded-lg"></div>
                        <div className="h-4 w-24 bg-white/30 rounded"></div>
                      </div>
                      <div className="flex gap-2">
                        <div className="w-8 h-8 bg-white/20 rounded-full"></div>
                        <div className="w-8 h-8 bg-white/20 rounded-full"></div>
                      </div>
                    </div>
                  </div>
                  {/* Mock Dashboard Content */}
                  <div className="p-4 md:p-6 space-y-4">
                    {/* Stats Row */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {['$12,450', '8 Jobs', '3 Pending', '$4,200'].map((stat, i) => (
                        <div key={i} className="bg-slate-50 rounded-xl p-3 text-center">
                          <div className="text-lg font-bold text-foreground">{stat}</div>
                          <div className="text-xs text-muted-foreground">
                            {['Revenue', 'Active', 'Estimates', 'Due'][i]}
                          </div>
                        </div>
                      ))}
                    </div>
                    {/* Mock List Items */}
                    <div className="space-y-2">
                      {[1, 2, 3].map((i) => (
                        <div key={i} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                            <FileText className="w-5 h-5 text-primary" />
                          </div>
                          <div className="flex-1">
                            <div className="h-3 bg-gray-200 rounded w-3/4 mb-1"></div>
                            <div className="h-2 bg-gray-100 rounded w-1/2"></div>
                          </div>
                          <div className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded">
                            Approved
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Floating Elements */}
              <div className="absolute -right-4 top-1/4 bg-white rounded-xl shadow-lg p-4 border border-gray-100 hidden lg:block animate-bounce-slow">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold">Estimate Approved!</div>
                    <div className="text-xs text-muted-foreground">John D. - $3,500</div>
                  </div>
                </div>
              </div>
              
              <div className="absolute -left-4 bottom-1/4 bg-white rounded-xl shadow-lg p-4 border border-gray-100 hidden lg:block animate-bounce-slow" style={{ animationDelay: '0.5s' }}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <CreditCard className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold">Payment Received</div>
                    <div className="text-xs text-muted-foreground">Invoice #1023 - $1,200</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trusted By / Social Proof */}
      <section className="py-12 bg-slate-50 border-y border-gray-100">
        <div className="max-w-7xl mx-auto px-4">
          <p className="text-center text-muted-foreground font-medium mb-8">
            Trusted by contractors across the country
          </p>
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16 opacity-60">
            {['Concrete', 'Roofing', 'Landscaping', 'Painting', 'Fencing', 'HVAC'].map((industry) => (
              <div key={industry} className="flex items-center gap-2 text-gray-500">
                <Shield className="w-5 h-5" />
                <span className="font-semibold">{industry}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-16 md:py-24 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-4">
              Everything You Need to Run Your Business
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              From first estimate to final payment, we've got you covered.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="group bg-white rounded-2xl p-6 md:p-8 border border-gray-100 hover:border-primary/20 hover:shadow-xl transition-all duration-300"
              >
                <div className={`w-14 h-14 ${feature.color} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                  <feature.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-heading text-xl font-semibold text-foreground mb-3">
                  {feature.title}
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 md:py-24 px-4 bg-gradient-to-b from-primary to-blue-800">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
                Stop Losing Money to Disorganization
              </h2>
              <p className="text-lg text-blue-100 mb-8">
                Most contractors lose hours every week on paperwork and chasing payments. 
                Our system automates the boring stuff so you can focus on the work that pays.
              </p>
              
              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-secondary rounded-full flex items-center justify-center flex-shrink-0">
                      <benefit.icon className="w-4 h-4 text-black" />
                    </div>
                    <span className="text-white font-medium">{benefit.text}</span>
                  </div>
                ))}
              </div>
              
              <Button 
                size="lg"
                onClick={handleDemo}
                className="mt-8 bg-secondary text-black hover:bg-secondary/90 h-14 px-8 text-lg font-bold shadow-lg"
                data-testid="benefits-demo-btn"
              >
                See It In Action
                <ChevronRight className="w-5 h-5 ml-1" />
              </Button>
            </div>
            
            <div className="relative">
              <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-6 md:p-8">
                <div className="bg-white rounded-2xl p-6 space-y-6">
                  <div className="flex items-center justify-between">
                    <h4 className="font-heading font-semibold text-lg">Monthly Savings</h4>
                    <span className="text-sm text-muted-foreground">Average user</span>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Time saved on estimates</span>
                      <span className="font-bold text-green-600">+8 hrs/mo</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Faster payments</span>
                      <span className="font-bold text-green-600">15 days avg</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Estimate conversion</span>
                      <span className="font-bold text-green-600">+23%</span>
                    </div>
                  </div>
                  
                  <div className="pt-4 border-t border-gray-100">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold">Estimated monthly value</span>
                      <span className="text-2xl font-bold text-primary">$1,200+</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-16 md:py-24 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-4">
              Simple, Transparent Pricing
            </h2>
            <p className="text-lg text-muted-foreground">
              No hidden fees. Cancel anytime.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6 md:gap-8 max-w-4xl mx-auto">
            {pricingTiers.map((tier, index) => (
              <div 
                key={index}
                className={`rounded-2xl p-6 md:p-8 ${
                  tier.highlighted 
                    ? 'bg-primary text-white ring-4 ring-primary/20 scale-105' 
                    : 'bg-white border border-gray-200'
                }`}
              >
                {tier.highlighted && (
                  <div className="text-center mb-4">
                    <span className="bg-secondary text-black text-xs font-bold px-3 py-1 rounded-full">
                      MOST POPULAR
                    </span>
                  </div>
                )}
                
                <h3 className={`font-heading text-xl font-semibold mb-2 ${tier.highlighted ? 'text-white' : 'text-foreground'}`}>
                  {tier.name}
                </h3>
                <p className={`text-sm mb-4 ${tier.highlighted ? 'text-blue-100' : 'text-muted-foreground'}`}>
                  {tier.description}
                </p>
                
                <div className="mb-6">
                  <span className={`text-4xl font-bold ${tier.highlighted ? 'text-white' : 'text-foreground'}`}>
                    ${tier.price}
                  </span>
                  <span className={tier.highlighted ? 'text-blue-100' : 'text-muted-foreground'}>
                    /month
                  </span>
                </div>
                
                <ul className="space-y-3 mb-8">
                  {tier.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <CheckCircle2 className={`w-5 h-5 flex-shrink-0 mt-0.5 ${tier.highlighted ? 'text-secondary' : 'text-green-500'}`} />
                      <span className={`text-sm ${tier.highlighted ? 'text-white' : 'text-foreground'}`}>
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>
                
                <Button 
                  onClick={handleGetStarted}
                  className={`w-full h-12 font-semibold ${
                    tier.highlighted 
                      ? 'bg-white text-primary hover:bg-gray-100' 
                      : 'bg-primary text-white hover:bg-primary/90'
                  }`}
                  data-testid={`pricing-${tier.name.toLowerCase()}-btn`}
                >
                  Get Started
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mobile QR Code Section */}
      <section className="py-16 md:py-24 px-4 bg-gradient-to-br from-slate-50 to-blue-50 overflow-hidden">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
            {/* Left side - Text content */}
            <div className="text-center md:text-left">
              <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-semibold mb-6">
                <Smartphone className="w-4 h-4" />
                Built for the Field
              </div>
              
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-4">
                Your Office in Your Pocket
              </h2>
              <p className="text-lg text-muted-foreground mb-6">
                ContractorOS is designed <strong>mobile-first</strong> for contractors who work on job sites, not behind desks. 
                Create estimates, schedule jobs, and send invoices right from your phone or tablet.
              </p>
              
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-3 justify-center md:justify-start">
                  <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                  </div>
                  <span className="text-foreground font-medium">Works offline in the field</span>
                </li>
                <li className="flex items-center gap-3 justify-center md:justify-start">
                  <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                  </div>
                  <span className="text-foreground font-medium">Install as an app on any device</span>
                </li>
                <li className="flex items-center gap-3 justify-center md:justify-start">
                  <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                  </div>
                  <span className="text-foreground font-medium">Large buttons built for work gloves</span>
                </li>
              </ul>
            </div>
            
            {/* Right side - QR Code */}
            <div className="flex justify-center">
              <div className="relative">
                {/* Decorative background elements */}
                <div className="absolute -inset-4 bg-gradient-to-r from-primary/20 to-blue-400/20 rounded-3xl blur-2xl" />
                <div className="absolute -inset-1 bg-gradient-to-r from-primary to-blue-400 rounded-2xl opacity-20" />
                
                {/* QR Code Card */}
                <div className="relative bg-white rounded-2xl p-6 md:p-8 shadow-2xl border border-gray-100">
                  <div className="text-center mb-4">
                    <div className="inline-flex items-center gap-2 text-primary font-semibold text-sm mb-2">
                      <ScanLine className="w-5 h-5" />
                      Scan to Try on Mobile
                    </div>
                  </div>
                  
                  {/* QR Code with styled corners */}
                  <div className="relative p-4 bg-gradient-to-br from-slate-50 to-white rounded-xl">
                    {/* Corner decorations */}
                    <div className="absolute top-0 left-0 w-6 h-6 border-t-4 border-l-4 border-primary rounded-tl-lg" />
                    <div className="absolute top-0 right-0 w-6 h-6 border-t-4 border-r-4 border-primary rounded-tr-lg" />
                    <div className="absolute bottom-0 left-0 w-6 h-6 border-b-4 border-l-4 border-primary rounded-bl-lg" />
                    <div className="absolute bottom-0 right-0 w-6 h-6 border-b-4 border-r-4 border-primary rounded-br-lg" />
                    
                    <QRCodeSVG 
                      value="https://app.birdenmedia.co/demo"
                      size={180}
                      level="H"
                      includeMargin={true}
                      fgColor="#1e3a8a"
                      bgColor="transparent"
                      className="mx-auto"
                    />
                  </div>
                  
                  <div className="text-center mt-4">
                    <p className="text-muted-foreground text-sm">
                      Point your camera at the code
                    </p>
                    <p className="text-foreground font-semibold mt-1">
                      Try the free demo instantly
                    </p>
                  </div>
                  
                  {/* Phone mockup hint */}
                  <div className="flex justify-center mt-4 gap-2">
                    <div className="w-2 h-2 rounded-full bg-primary/30" />
                    <div className="w-2 h-2 rounded-full bg-primary/50" />
                    <div className="w-2 h-2 rounded-full bg-primary" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 px-4 bg-gradient-to-br from-slate-900 to-slate-800">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Simplify Your Business?
          </h2>
          <p className="text-lg text-slate-300 mb-8 max-w-2xl mx-auto">
            Join hundreds of contractors who've ditched the paperwork and started getting paid faster.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg"
              onClick={handleDemo}
              className="bg-secondary text-black hover:bg-secondary/90 h-14 px-8 text-lg font-bold"
              data-testid="cta-demo-btn"
            >
              Try Free Demo
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button 
              size="lg"
              variant="outline"
              onClick={handleGetStarted}
              className="h-14 px-8 text-lg font-semibold bg-transparent border-white text-white hover:bg-white/10"
            >
              Sign In
            </Button>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section id="contact-form" className="py-24 px-4 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1 bg-primary/10 text-primary rounded-full text-sm font-medium mb-4">
              Get Started
            </span>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Ready to Streamline Your Business?
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Fill out the form below and we'll reach out to set up your personalized portal.
              No commitment, no pressure - just a conversation about how we can help.
            </p>
          </div>
          
          {formSubmitted ? (
            <div className="bg-green-50 border border-green-200 rounded-2xl p-12 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-2xl font-bold text-green-800 mb-2">Thank You!</h3>
              <p className="text-green-700">
                We've received your information and will be in touch within 24 hours.
              </p>
              <Button 
                onClick={() => setFormSubmitted(false)}
                variant="outline"
                className="mt-6"
              >
                Submit Another Request
              </Button>
            </div>
          ) : (
            <form onSubmit={handleContactSubmit} className="bg-white rounded-2xl shadow-xl border border-slate-200 p-8 md:p-12">
              <div className="grid md:grid-cols-2 gap-6">
                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Your Name <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      type="text"
                      placeholder="John Smith"
                      value={contactForm.name}
                      onChange={(e) => setContactForm({...contactForm, name: e.target.value})}
                      className="pl-10 h-12"
                      required
                      data-testid="contact-name"
                    />
                  </div>
                </div>
                
                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      type="email"
                      placeholder="john@company.com"
                      value={contactForm.email}
                      onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                      className="pl-10 h-12"
                      required
                      data-testid="contact-email"
                    />
                  </div>
                </div>
                
                {/* Company */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Company Name <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      type="text"
                      placeholder="Smith Roofing LLC"
                      value={contactForm.company}
                      onChange={(e) => setContactForm({...contactForm, company: e.target.value})}
                      className="pl-10 h-12"
                      required
                      data-testid="contact-company"
                    />
                  </div>
                </div>
                
                {/* Niche/Industry */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Industry / Niche
                  </label>
                  <select
                    value={contactForm.niche}
                    onChange={(e) => setContactForm({...contactForm, niche: e.target.value})}
                    className="w-full h-12 px-3 rounded-md border border-slate-300 bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    data-testid="contact-niche"
                  >
                    <option value="">Select your industry...</option>
                    {niches.map(niche => (
                      <option key={niche} value={niche}>{niche}</option>
                    ))}
                  </select>
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Phone</label>
                  <Input
                    type="tel"
                    placeholder="(555) 123-4567"
                    value={contactForm.phone}
                    onChange={(e) => setContactForm({...contactForm, phone: e.target.value})}
                    className="h-12"
                    data-testid="contact-phone"
                  />
                </div>

                {/* Team size */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Team size</label>
                  <select
                    value={contactForm.employees_count}
                    onChange={(e) => setContactForm({...contactForm, employees_count: e.target.value})}
                    className="w-full h-12 px-3 rounded-md border border-slate-300 bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    data-testid="contact-team-size"
                  >
                    <option value="">How many on your team?</option>
                    <option value="solo">Just me</option>
                    <option value="2-5">2 – 5</option>
                    <option value="6-15">6 – 15</option>
                    <option value="16+">16+</option>
                  </select>
                </div>
              </div>

              {/* Message */}
              <div className="mt-6">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Tell us about your business <span className="text-slate-400 font-normal">(optional)</span>
                </label>
                <textarea
                  placeholder="What does your business do, what tools do you use today, and what's your biggest pain point?"
                  value={contactForm.message}
                  onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                  rows={4}
                  className="w-full px-3 py-2 rounded-md border border-slate-300 bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-y"
                  data-testid="contact-message"
                  maxLength={1000}
                />
                <p className="text-xs text-slate-400 mt-1">{(contactForm.message || '').length}/1000</p>
              </div>
              
              <div className="mt-8 flex flex-col sm:flex-row gap-4 items-center justify-between">
                <p className="text-sm text-slate-500">
                  We'll never share your information with third parties.
                </p>
                <Button 
                  type="submit"
                  size="lg"
                  disabled={isSubmitting}
                  className="w-full sm:w-auto h-12 px-8 bg-primary hover:bg-primary/90"
                  data-testid="contact-submit"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      Get in Touch
                    </>
                  )}
                </Button>
              </div>
            </form>
          )}
        </div>
      </section>

      {/* Contact / Footer */}
      <footer id="contact" className="bg-slate-900 text-white py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-12">
            {/* Brand - ContractorOS */}
            <div className="md:col-span-2">
              <div className="flex items-center gap-2 mb-4">
                <span className="text-2xl font-black tracking-tight">
                  <span className="bg-gradient-to-r from-primary to-blue-400 bg-clip-text text-transparent">Contractor</span>
                  <span className="text-white">OS</span>
                </span>
                <span className="text-xs text-slate-400 uppercase tracking-wider ml-2">by BirdenMedia</span>
              </div>
              <p className="text-slate-400 max-w-sm mb-6">
                The all-in-one client portal for contractors. 
                Professional estimates, invoicing, job scheduling, and more.
              </p>
              
              {/* Newsletter */}
              <div className="flex gap-2">
                <Input 
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 h-12"
                  data-testid="footer-email-input"
                />
                <Button className="bg-primary hover:bg-primary/90 h-12 px-6">
                  Subscribe
                </Button>
              </div>
            </div>
            
            {/* Quick Links */}
            <div>
              <h4 className="font-semibold mb-4">Product</h4>
              <ul className="space-y-2 text-slate-400">
                <li><a href="#features" className="hover:text-white transition-colors">Features</a></li>
                <li><a href="#pricing" className="hover:text-white transition-colors">Pricing</a></li>
                <li><button onClick={handleDemo} className="hover:text-white transition-colors">Demo</button></li>
              </ul>
            </div>
            
            {/* Contact */}
            <div>
              <h4 className="font-semibold mb-4">Contact</h4>
              <ul className="space-y-3 text-slate-400">
                <li className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  <a href="mailto:hello@birdenmedia.co" className="hover:text-white transition-colors">
                    hello@birdenmedia.co
                  </a>
                </li>
              </ul>
            </div>
          </div>
          
          {/* Bottom */}
          <div className="border-t border-slate-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-slate-500 text-sm">
              © {new Date().getFullYear()} ContractorOS by BirdenMedia. All rights reserved.
            </p>
            <div className="flex gap-6 text-sm text-slate-500">
              <a href="#" className="hover:text-white transition-colors">Privacy Policy</a>
              <a href="#" className="hover:text-white transition-colors">Terms of Service</a>
            </div>
          </div>
        </div>
      </footer>
      
      {/* Custom Animations */}
      <style>{`
        @keyframes bounce-slow {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-10px);
          }
        }
        .animate-bounce-slow {
          animation: bounce-slow 3s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default LandingPage;
